import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { flowService } from '../services/flowService';

/**
 * On opening a flow, check in the background whether its local JSON has drifted from the version
 * stored on WhatsApp (someone may have edited it in WhatsApp Manager). While the check is in flight,
 * and until the user reconciles a detected difference, `driftResolved` is false so the builder can
 * suppress saving and avoid clobbering the remote edit.
 *
 * Only SYNCED flows with a Meta id are checked: a PENDING/SYNCING flow's local≠remote is just our own
 * not-yet-uploaded edit, and a never-synced flow has no remote. A failed check resolves open (we never
 * trap the user behind a flaky network/Meta call).
 *
 * @returns {{ driftResolved: boolean, drift: {remoteJson: object}|null, resolve: () => void }}
 */
export function useDriftCheck(currentFlow) {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const [driftResolved, setDriftResolved] = useState(true);
  const [drift, setDrift] = useState(null);
  const checkedFlowIdRef = useRef(null);

  // Read these from refs inside the effect so their identity (which can change every render) does not
  // re-run the effect mid-check, a re-run would cleanup-cancel the in-flight request and leave us
  // stuck blocking saves forever.
  const authFetchRef = useRef(authenticatedFetch);
  authFetchRef.current = authenticatedFetch;
  const envIdRef = useRef(selectedEnvironment?.id);
  envIdRef.current = selectedEnvironment?.id;

  const eligible =
    !!currentFlow?.id &&
    !!currentFlow?.whatsappFlowId &&
    currentFlow?.syncStatus === 'SYNCED' &&
    !!selectedEnvironment?.id;

  useEffect(() => {
    if (!eligible) {
      // Nothing to compare (new/unsynced flow, or our own changes still pending), saving is allowed.
      setDrift(null);
      setDriftResolved(true);
      return undefined;
    }
    // Run the check once per opened flow; don't re-check after our own saves re-sync it.
    if (checkedFlowIdRef.current === currentFlow.id) {
      return undefined;
    }
    checkedFlowIdRef.current = currentFlow.id;

    let cancelled = false;
    setDrift(null);
    setDriftResolved(false); // block saving until the check resolves
    flowService
      .checkDrift(envIdRef.current, currentFlow.id, authFetchRef.current)
      .then((result) => {
        if (cancelled) return;
        if (result.inSync || !result.remoteAvailable) {
          setDriftResolved(true);
        } else {
          // Stays unresolved until the user picks a reconciliation direction.
          setDrift({ remoteJson: result.remoteJson });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('WhatsApp drift check failed; allowing edits:', err);
        setDriftResolved(true);
      });

    return () => {
      cancelled = true;
    };
    // Intentionally keyed only on eligibility + flow id (auth/env read via refs); see note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, currentFlow?.id]);

  // Called by the reconciliation dialog after the user has chosen a direction.
  const resolve = useCallback(() => {
    setDrift(null);
    setDriftResolved(true);
  }, []);

  return { driftResolved, drift, resolve };
}
